import {
  BoardState,
  Move,
  Square,
  Color,
  PieceType,
  GameStatus,
  KingMoveStreak,
  VARIANT_CONFIGS,
  VARIANT_SPECS,
  hasStandardRules,
  FRISIAN_TWO_VS_ONE_PLY_LIMIT,
} from '@draughts/shared';
import {
  getPieceAt,
  removePieceAt,
  setPieceAt,
  cloneBoard,
  countPieces,
  countKings,
} from '../board/board-utils.js';
import { getAllLegalMoves, getCaptureMoves } from '../moves/capture-moves.js';
import { serializePosition } from './position-hash.js';

/*
 * `FRISIAN_TWO_VS_ONE_PLY_LIMIT` (14 = 7 mosse del lato forte) viveva qui come
 * const privato. Sta in `@draughts/shared/constants/patta.ts` perché lo legge
 * anche lo SCHERMO: la riga del conteggio in partita scrive «2/7», e quel 7 lo
 * deriva da qui. Con due copie, il giorno che il numero cambia la riga
 * mentirebbe mentre la partita si comporta bene — il modo peggiore in cui può
 * andare. Il commento che spiega la regola è rimasto accanto alla costante.
 */

/**
 * Perché una partita è finita.
 *
 * `GameStatus` dice CHI ha vinto, mai perché: senza questo, ogni schermata di fine
 * partita doveva indovinare il motivo, e quella locale mostrava "il Nero non ha più
 * mosse" anche dopo un abbandono o una patta accordata.
 *
 * - `no_moves`      — `side` è al tratto e non ha mosse legali (in antidama è chi VINCE).
 * - `no_pieces`     — `side` ha perso tutti i pezzi (idem).
 * - `first_king`    — breakthrough: `side` ha promosso per primo e vince all'istante.
 * - `move_limit`    — troppe semi-mosse senza catture (il limite dipende dalla variante).
 * - `repetition`    — stessa posizione per la terza volta.
 * - `endgame_draw`  — patta materiale Frisian (dama vs dama, o 2 dame vs 1 fuori tempo).
 * - `zugzwang`      — Frisian, dame su 5 e 46: `side` è al tratto ed è costretto a perdere.
 * - `lone_king`     — aggiudicazione turca: la dama sola di `side` batte la pedina sola.
 * - `single_piece`  — aggiudicazione ghanese: `side` è rimasto con un solo pezzo e perde.
 */
export type GameEndReasonKind =
  | 'no_moves'
  | 'no_pieces'
  | 'first_king'
  | 'move_limit'
  | 'repetition'
  | 'endgame_draw'
  | 'zugzwang'
  | 'lone_king'
  | 'single_piece';

export interface GameEndReason {
  kind: GameEndReasonKind;
  /** Lato a cui il motivo si riferisce; assente per i motivi che non ne hanno uno. */
  side?: Color;
}

/** Stato terminale + motivo. `reason` è null solo quando la partita continua. */
export interface GameOutcome {
  status: GameStatus;
  reason: GameEndReason | null;
}

// Esiti senza lato: costanti condivise, così il caso caldo (partita in corso, valutato
// a ogni nodo della ricerca) non alloca nulla.
const PLAYING_OUTCOME: GameOutcome = Object.freeze({ status: GameStatus.PLAYING, reason: null });
const MOVE_LIMIT_DRAW: GameOutcome = Object.freeze({
  status: GameStatus.DRAW,
  reason: Object.freeze({ kind: 'move_limit' }) as GameEndReason,
});
const REPETITION_DRAW: GameOutcome = Object.freeze({
  status: GameStatus.DRAW,
  reason: Object.freeze({ kind: 'repetition' }) as GameEndReason,
});
const ENDGAME_DRAW: GameOutcome = Object.freeze({
  status: GameStatus.DRAW,
  reason: Object.freeze({ kind: 'endgame_draw' }) as GameEndReason,
});

interface ColorMaterial {
  men: number;
  kings: number;
}

function materialOf(board: BoardState, color: Color): ColorMaterial {
  const kings = countKings(board, color);
  return { kings, men: countPieces(board, color) - kings };
}

/** True se un lato ha esattamente 2 dame (0 pedine) e l'altro 1 dama (0 pedine). */
function isTwoKingsVsOneKing(board: BoardState): boolean {
  const w = materialOf(board, Color.WHITE);
  const b = materialOf(board, Color.BLACK);
  if (w.men !== 0 || b.men !== 0) return false;
  return (w.kings === 2 && b.kings === 1) || (w.kings === 1 && b.kings === 2);
}

/** True se entrambi i lati hanno esattamente 1 dama e nessuna pedina. */
function isKingVsKing(board: BoardState): boolean {
  const w = materialOf(board, Color.WHITE);
  const b = materialOf(board, Color.BLACK);
  return w.men === 0 && b.men === 0 && w.kings === 1 && b.kings === 1;
}

/** True se il colore indicato ha almeno una cattura disponibile nella posizione (turno ignorato). */
function sideHasCapture(board: BoardState, color: Color): boolean {
  return board.pieces.some(
    (p) => p.color === color && getCaptureMoves(board, p.square).length > 0,
  );
}

/**
 * Applica una mossa alla scacchiera e ritorna il nuovo stato
 * Gestisce:
 * - Movimento del pezzo
 * - Rimozione pezzi catturati
 * - Promozioni
 * - Cambio turno
 * - Aggiornamento storico mosse
 * - Aggiornamento halfMoveClock (per regola patta)
 *
 * @param board - Stato corrente della scacchiera
 * @param move - Mossa da applicare
 * @returns Nuovo stato della scacchiera dopo la mossa
 * @throws Error se la mossa non è valida
 */
export function applyMove(board: BoardState, move: Move): BoardState {
  // Verifica che la mossa sia valida
  if (!isValidMove(board, move)) {
    throw new Error(
      `Invalid move: ${move.from} -> ${move.to}. Move is not legal in current position.`
    );
  }

  // Inizia con una copia della board
  let newBoard = cloneBoard(board);

  // 1. Ottieni il pezzo che si muove
  const movingPiece = getPieceAt(board, move.from);
  if (!movingPiece) {
    throw new Error(`No piece found at square ${move.from}`);
  }

  // 2. Rimuovi il pezzo dalla posizione originale
  newBoard = removePieceAt(newBoard, move.from);

  // 3. Rimuovi tutti i pezzi catturati
  for (const capture of move.captures) {
    newBoard = removePieceAt(newBoard, capture.square);
  }

  // 4. Determina il tipo del pezzo nella nuova posizione
  const finalPieceType = move.isPromotion ? PieceType.KING : movingPiece.type;

  // 5. Posiziona il pezzo nella nuova posizione
  newBoard = setPieceAt(newBoard, {
    color: movingPiece.color,
    type: finalPieceType,
    square: move.to,
  });

  // 6. Aggiorna halfMoveClock (per regola 25 mosse senza cattura)
  newBoard.halfMoveClock = nextHalfMoveClock(board.halfMoveClock, move, movingPiece.type);

  // 7. Aggiungi la mossa allo storico
  newBoard.moveHistory = [...newBoard.moveHistory, move];

  // 8. Cambia il turno
  newBoard.currentTurn =
    board.currentTurn === Color.WHITE ? Color.BLACK : Color.WHITE;

  // 9. Aggiungi posizione corrente allo storico per threefold repetition
  const positionHash = serializePosition(newBoard);
  newBoard.positionHistory = [...(newBoard.positionHistory ?? []), positionHash];

  // 9b. Aggiorna lo stato delle regole Frisian (chi ha mosso è board.currentTurn, pre-cambio turno)
  updateFrisianState(board, newBoard, move, movingPiece.type);

  // 10. Verifica se la partita è finita
  newBoard.status = determineGameStatus(newBoard);

  return newBoard;
}

/**
 * Aggiorna lo stato delle regole Frisian su `newBoard` dopo l'applicazione di `move`.
 * - kingMoveStreak: mosse silenziose consecutive per dama, per colore (regola dama bloccata).
 * - frisianEndgamePly: ply di inizio della configurazione 2-dame-vs-1-dama (regola 7 mosse).
 *
 * @param prevBoard - stato pre-mossa (il mover è prevBoard.currentTurn)
 * @param newBoard - stato post-mossa (moveHistory già aggiornato, turno già cambiato)
 * @param move - mossa applicata
 * @param movingPieceType - tipo del pezzo che ha mosso (prima dell'eventuale promozione)
 */
export function updateFrisianState(
  prevBoard: BoardState,
  newBoard: BoardState,
  move: Move,
  movingPieceType: PieceType,
): void {
  const rules = VARIANT_SPECS[newBoard.variant].rules;

  // --- Regola dama bloccata: streak di mosse silenziose per il colore che ha mosso ---
  if (rules.blockedKingMaxMoves && rules.blockedKingMaxMoves > 0) {
    const mover = prevBoard.currentTurn;
    const prevStreak = prevBoard.kingMoveStreak ?? {};
    let moverStreak: KingMoveStreak | null;

    if (move.captures.length > 0) {
      moverStreak = null; // una cattura azzera il contatore
    } else if (movingPieceType === PieceType.KING) {
      const existing = prevStreak[mover];
      moverStreak =
        existing && existing.square === move.from
          ? { square: move.to, count: existing.count + 1 }
          : { square: move.to, count: 1 };
    } else {
      moverStreak = null; // ha mosso una pedina: contatore della dama azzerato
    }

    newBoard.kingMoveStreak = { ...prevStreak, [mover]: moverStreak };
  }

  // --- Regola 2 dame vs 1 dama: marca il ply di inizio della configurazione ---
  if (rules.frisianEndgameDraws) {
    if (isTwoKingsVsOneKing(newBoard)) {
      const wasTwoVsOne = isTwoKingsVsOneKing(prevBoard);
      newBoard.frisianEndgamePly =
        wasTwoVsOne && prevBoard.frisianEndgamePly != null
          ? prevBoard.frisianEndgamePly
          : newBoard.moveHistory.length;
    } else {
      newBoard.frisianEndgamePly = null;
    }
  }
}

/**
 * Verifica se una mossa è legale nella posizione corrente
 *
 * @param board - Stato corrente della scacchiera
 * @param move - Mossa da verificare
 * @returns true se la mossa è legale, false altrimenti
 */
export function isValidMove(board: BoardState, move: Move): boolean {
  // 1. Verifica che ci sia un pezzo nella casella di partenza
  const piece = getPieceAt(board, move.from);
  if (!piece) {
    return false;
  }

  // 2. Verifica che il pezzo sia del colore del turno corrente
  if (piece.color !== board.currentTurn) {
    return false;
  }

  // 3. Verifica che la partita sia in corso
  if (board.status !== GameStatus.PLAYING) {
    return false;
  }

  // 4. Ottieni tutte le mosse legali
  const legalMoves = getAllLegalMoves(board);

  // 5. Verifica se la mossa è tra quelle legali
  // Confronta from, to, e le catture
  return legalMoves.some((legalMove) => {
    if (legalMove.from !== move.from || legalMove.to !== move.to) {
      return false;
    }

    // Verifica che le catture siano le stesse
    if (legalMove.captures.length !== move.captures.length) {
      return false;
    }

    // Verifica che ogni cattura sia presente
    const legalCaptureSquares = legalMove.captures.map((c) => c.square).sort();
    const moveCaptureSquares = move.captures.map((c) => c.square).sort();

    if (!legalCaptureSquares.every((sq, idx) => sq === moveCaptureSquares[idx])) {
      return false;
    }

    /*
     * Anche la PROMOZIONE deve combaciare.
     *
     * `applyMove` verifica la mossa con questa funzione e poi applica l'oggetto
     * del CHIAMANTE (`finalPieceType = move.isPromotion ? KING : …`): senza questo
     * confronto una mossa legale con `isPromotion: true` aggiunto a mano passava, e
     * la pedina si trasformava in dama in mezzo alla damiera. Una dama gratis, dal
     * nulla.
     *
     * Oggi nessun percorso ci arriva — la stanza, il validatore dei puzzle e il
     * servizio dei puzzle rimatchano tutti la mossa contro `getAllLegalMoves` e
     * passano l'oggetto DEL MOTORE, buttando quello del client — quindi non è una
     * falla aperta, è una trappola armata per il prossimo chiamante che si fidi
     * del contratto («se `isValidMove` dice sì, applicala»).
     *
     * `!!` su entrambi i lati: nei record PDN e nei replay vecchi il campo può
     * essere assente, e `undefined !== false` farebbe cadere mosse che oggi si
     * accettano. È lo stesso valore, scritto in due modi.
     */
    return !!legalMove.isPromotion === !!move.isPromotion;
  });
}

/**
 * Determina lo stato della partita dopo una mossa
 *
 * @param board - Stato della scacchiera dopo la mossa
 * @returns Nuovo stato della partita
 */
export function determineGameStatus(board: BoardState): GameStatus {
  return determineGameOutcome(board).status;
}

/**
 * Il contatore delle semi-mosse senza progresso, dopo `move`.
 *
 * Si azzera su una cattura O sul movimento di una PEDINA, e cresce solo sulle
 * mosse di dama senza cattura: è quel contatore che `determineGameOutcome`
 * confronta con `drawMoveLimit`.
 *
 * Esportata perché la ricerca (`ai/minimax.applyMoveToBoard`) ne teneva una
 * COPIA che implementava metà della regola — azzerava solo sulla cattura — e il
 * risultato era che l'AI vedeva la patta per non-progresso arrivare quando non
 * era vera: a parità di posizione e profondità scegliva mosse diverse nei finali
 * a seconda del contatore di partenza. È la stessa deriva che il commento in
 * `applyMoveToBoard` rivendica di avere evitato per lo `status`, e che per il
 * contatore che quello status legge non era stata evitata.
 *
 * `tipoPezzoInMovimento` è il tipo PRIMA della mossa: una pedina che promuove
 * azzera comunque, perché il progresso c'è stato.
 */
export function nextHalfMoveClock(
  halfMoveClockPrecedente: number,
  move: Move,
  tipoPezzoInMovimento: PieceType,
): number {
  if (move.captures.length > 0 || tipoPezzoInMovimento === PieceType.MAN) return 0;
  return halfMoveClockPrecedente + 1;
}

/**
 * Come `determineGameStatus`, ma dice anche PERCHÉ. Unica implementazione delle due:
 * un secondo calcolo del motivo, a parte, sarebbe derivato dallo stato e prima o poi
 * avrebbe smesso di corrispondergli.
 */
export function determineGameOutcome(board: BoardState): GameOutcome {
  // Modalità di gioco (modificatore ortogonale della condizione di vittoria).
  // L'inversione è fatta QUI, così GameStatus significa sempre "chi vince davvero":
  // tutto il downstream (result, rating, label client) resta mode-agnostic.
  const mode = board.ruleMode ?? 'normal';
  const antidraughts = mode === 'antidraughts';

  // 0. Breakthrough: la prima dama (promozione uomo→dama) vince all'istante.
  //    Si guarda l'EVENTO di promozione dell'ultima mossa, non "esiste una dama"
  //    (alcune varianti partono con dame già in campo).
  if (mode === 'breakthrough') {
    const lastMove = board.moveHistory[board.moveHistory.length - 1];
    if (lastMove?.isPromotion) {
      // Vince chi ha appena mosso = opposto del lato ora al tratto (turno già ruotato).
      const promoter = board.currentTurn === Color.WHITE ? Color.BLACK : Color.WHITE;
      return {
        status: promoter === Color.WHITE ? GameStatus.WHITE_WINS : GameStatus.BLACK_WINS,
        reason: { kind: 'first_king', side: promoter },
      };
    }
  }

  // 1. Verifica se il giocatore corrente ha mosse legali
  const legalMoves = getAllLegalMoves(board);

  if (legalMoves.length === 0) {
    // Normale: il giocatore al tratto non può muovere ⇒ ha perso.
    // Antidraughts: chi resta bloccato ⇒ VINCE (misère).
    const whiteWins = antidraughts
      ? board.currentTurn === Color.WHITE
      : board.currentTurn === Color.BLACK;
    return {
      status: whiteWins ? GameStatus.WHITE_WINS : GameStatus.BLACK_WINS,
      // Il lato è sempre quello RIMASTO SENZA MOSSE, vinca o perda: è il fatto,
      // e il titolo del risultato dice già chi ha vinto. Chi non ha più PEZZI non ha
      // nemmeno mosse e finirebbe qui: si distingue perché "non ha più pezzi" dice
      // molto di più di "non ha più mosse" (l'esito è identico in entrambe le modalità).
      reason: {
        kind: countPieces(board, board.currentTurn) === 0 ? 'no_pieces' : 'no_moves',
        side: board.currentTurn,
      },
    };
  }

  // 2. Verifica regola delle mosse senza cattura (varia per variante)
  // International: 50 halfmoves = 25 mosse complete
  // Italian: 80 halfmoves = 40 mosse complete (FID 2025)
  // drawMoveLimit <= 0 means the variant has no N-move draw rule (disabled)
  const config = VARIANT_CONFIGS[board.variant];
  if (config.drawMoveLimit > 0 && board.halfMoveClock >= config.drawMoveLimit) {
    return MOVE_LIMIT_DRAW;
  }

  // 3. Verifica se un giocatore ha perso tutti i pezzi
  // Antidraughts: restare senza pezzi ⇒ VINCE quel lato.
  const whitePieces = countPieces(board, Color.WHITE);
  const blackPieces = countPieces(board, Color.BLACK);

  if (whitePieces === 0) {
    return {
      status: antidraughts ? GameStatus.WHITE_WINS : GameStatus.BLACK_WINS,
      reason: { kind: 'no_pieces', side: Color.WHITE },
    };
  }

  if (blackPieces === 0) {
    return {
      status: antidraughts ? GameStatus.BLACK_WINS : GameStatus.WHITE_WINS,
      reason: { kind: 'no_pieces', side: Color.BLACK },
    };
  }

  // 3b. Regole di patta materiali Frisian (gate sulla variante)
  const rules = VARIANT_SPECS[board.variant].rules;
  if (rules.frisianEndgameDraws) {
    if (isKingVsKing(board)) {
      // Eccezione (fonte): dame sugli estremi della gran diagonale (5 e 46) =
      // zugzwang, il giocatore di turno perde (nessuno dei due può saltare —
      // manca la casella d'atterraggio — ma chi muove abbandona l'estremo e
      // viene catturato).
      const sqs = board.pieces.map((p) => p.square).sort((a, b) => a - b);
      if (sqs[0] === 5 && sqs[1] === 46) {
        return {
          status: board.currentTurn === Color.WHITE ? GameStatus.BLACK_WINS : GameStatus.WHITE_WINS,
          reason: { kind: 'zugzwang', side: board.currentTurn },
        };
      }
      // Dama vs dama: patta quando IL GIOCATORE DI TURNO non può catturare
      // (fonte: "il giocatore di turno non sia in grado di catturare").
      if (!sideHasCapture(board, board.currentTurn)) {
        return ENDGAME_DRAW;
      }
    }

    // 2 dame vs 1 dama: patta se il lato forte non ha vinto entro 7 mosse (14 ply)
    if (
      isTwoKingsVsOneKing(board) &&
      board.frisianEndgamePly != null &&
      board.moveHistory.length - board.frisianEndgamePly >= FRISIAN_TWO_VS_ONE_PLY_LIMIT
    ) {
      return ENDGAME_DRAW;
    }
  }

  // 3c. Adjudication turca: dama sola contro pedina sola = vittoria della dama
  //     (fonte cartacea + Mindsports). Solo con le REGOLE STANDARD: in
  //     antidraughts restare con meno pezzi è un vantaggio, in breakthrough la
  //     pedina sola può ancora promuovere e vincere.
  //
  //     `hasStandardRules`, non `=== 'normal'`: la modalità `blind` cambia come
  //     la damiera si DISEGNA, non chi vince, e `game-mode.ts` lo dichiara a
  //     lettere maiuscole («THE ONLY MODE THAT CHANGES NO RULE … must go through
  //     hasStandardRules rather than compare against 'normal'»). Col confronto
  //     secco una partita Turca o Ghanese alla cieca non si aggiudicava affatto:
  //     la posizione restava PLAYING, e sulla Turca (`drawMoveLimit: 0`) la
  //     partita andava a finire all'orologio.
  if (rules.loneKingVsLoneManWins && hasStandardRules(mode) && whitePieces === 1 && blackPieces === 1) {
    const white = board.pieces.find((p) => p.color === Color.WHITE)!;
    const black = board.pieces.find((p) => p.color === Color.BLACK)!;
    if (white.type === PieceType.KING && black.type === PieceType.MAN) {
      return { status: GameStatus.WHITE_WINS, reason: { kind: 'lone_king', side: Color.WHITE } };
    }
    if (black.type === PieceType.KING && white.type === PieceType.MAN) {
      return { status: GameStatus.BLACK_WINS, reason: { kind: 'lone_king', side: Color.BLACK } };
    }
  }

  // 3d. Adjudication ghanese: chi resta con un solo pezzo (pedina O dama) perde
  //     immediatamente (docs/rules/nigerian-ghanaian-rules-spec.md §1.7). Solo con
  //     le REGOLE STANDARD: in antidraughts restare con meno pezzi è l'obiettivo.
  //     `hasStandardRules` e non `=== 'normal'`, per la stessa ragione di 3c.
  //     Caso teorico entrambi-a-1: perde il lato al tratto (allineato al C++
  //     isVariantTerminal); in pratica irraggiungibile, il primo che scende a 1
  //     viene adjudicato subito.
  if (rules.singlePieceLoses && hasStandardRules(mode)) {
    if (whitePieces === 1 && blackPieces === 1) {
      return {
        status: board.currentTurn === Color.WHITE ? GameStatus.BLACK_WINS : GameStatus.WHITE_WINS,
        reason: { kind: 'single_piece', side: board.currentTurn },
      };
    }
    if (whitePieces === 1) {
      return { status: GameStatus.BLACK_WINS, reason: { kind: 'single_piece', side: Color.WHITE } };
    }
    if (blackPieces === 1) {
      return { status: GameStatus.WHITE_WINS, reason: { kind: 'single_piece', side: Color.BLACK } };
    }
  }

  // 4. Verifica threefold repetition (stessa posizione 3 volte)
  if (hasThreefoldRepetition(board)) {
    return REPETITION_DRAW;
  }

  // La partita continua
  return PLAYING_OUTCOME;
}

/**
 * Verifica se la posizione corrente si è ripetuta 3 volte
 * (regola patta per ripetizione)
 *
 * La posizione include:
 * - Disposizione dei pezzi
 * - Turno di gioco
 *
 * @param board - Stato corrente della scacchiera
 * @returns true se c'è una ripetizione tripla
 */
function hasThreefoldRepetition(board: BoardState): boolean {
  const history = board.positionHistory ?? [];
  /*
   * Sotto le nove voci una ripetizione tripla non può esistere. Non è una stima:
   * è aritmetica della dama.
   *
   * Una posizione non può ripetersi dopo due semi-mosse — nessuna singola mossa
   * si annulla da sé, e le pedine non tornano indietro — quindi due occorrenze
   * della stessa posizione distano ALMENO quattro semi-mosse. Per averne tre
   * servono quindi almeno otto semi-mosse, cioè nove voci contando la posizione
   * di partenza (che ora `createInitialBoard` semina).
   *
   * La soglia era 5, tarata su uno storico che NON conteneva la posizione
   * iniziale. Alzarla non salta nessuna patta e toglie la scansione completa a
   * tutti i nodi brevi — che nella ricerca dell'AI sono la maggioranza, ed è lì
   * che questa funzione viene chiamata milioni di volte.
   */
  if (history.length < 9) {
    return false;
  }

  const currentPosition = serializePosition(board);

  // Conta quante volte la posizione corrente appare nello storico
  let repetitionCount = 0;
  for (const pos of history) {
    if (pos === currentPosition) {
      repetitionCount++;
    }
  }

  // La posizione corrente è già stata aggiunta allo storico in applyMove,
  // quindi se appare 3 volte, abbiamo threefold repetition
  return repetitionCount >= 3;
}

/**
 * Serializza la posizione della scacchiera in una stringa
 * per permettere il confronto di posizioni
 *
 * Include:
 * - Posizione di tutti i pezzi
 * - Turno corrente (stessa posizione con turno diverso = posizione diversa)
 *
 * @param board - Stato della scacchiera
 * @returns Stringa che rappresenta la posizione
 */
export { serializePosition } from './position-hash.js';

/**
 * Annulla l'ultima mossa e ritorna allo stato precedente
 *
 * @param board - Stato corrente della scacchiera
 * @returns Stato della scacchiera prima dell'ultima mossa, o null se non c'è storico
 */
export function undoMove(board: BoardState): BoardState | null {
  if (board.moveHistory.length === 0) {
    return null; // Nessuna mossa da annullare
  }

  // Per annullare una mossa, dovremmo ricostruire lo stato precedente
  // Questo richiede di salvare più informazioni o di ricostruire dall'inizio

  // Per ora, implementiamo una versione che richiede di ricostruire dalla posizione iniziale
  // Una implementazione più efficiente salverebbe gli stati precedenti

  // TODO: Implementazione efficiente dell'undo
  // Per ora ritorniamo null per indicare che non è implementato
  return null;
}

/**
 * Verifica se una mossa specifica può essere eseguita
 * (alias per isValidMove per chiarezza)
 *
 * @param board - Stato corrente della scacchiera
 * @param from - Casella di partenza
 * @param to - Casella di arrivo
 * @returns true se esiste una mossa legale da from a to
 */
export function canMovePiece(
  board: BoardState,
  from: Square,
  to: Square
): boolean {
  const legalMoves = getAllLegalMoves(board);
  return legalMoves.some((move) => move.from === from && move.to === to);
}

/**
 * Ottiene la mossa specifica tra due caselle, se esiste
 *
 * @param board - Stato corrente della scacchiera
 * @param from - Casella di partenza
 * @param to - Casella di arrivo
 * @returns La mossa se esiste, null altrimenti
 */
export function getMove(
  board: BoardState,
  from: Square,
  to: Square
): Move | null {
  const legalMoves = getAllLegalMoves(board);
  return legalMoves.find((move) => move.from === from && move.to === to) ?? null;
}
